import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock, LogIn } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from 'react-i18next';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const { login, resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setErrorCode('validation/empty_fields');
      return;
    }

    setLoading(true);
    setErrorCode('');
    setSuccessMessage('');

    try {
      await login(email, password);
    } catch (error: any) {
      setErrorCode(error.code || 'unknown');
    }

    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setErrorCode('validation/empty_email');
      return;
    }

    setLoading(true);
    setErrorCode('');

    try {
      await resetPassword(email);
      setSuccessMessage('password_reset_sent');
    } catch (error: any) {
      setErrorCode(error.code || 'unknown');
    }

    setLoading(false);
  };

  const getErrorMessage = (code: string) => {
    if (!code) return '';
    
    switch (code) {
      case 'auth/user-not-found':
        return t('login.errors.user_not_found');
      case 'auth/wrong-password':
        return t('login.errors.wrong_password');
      case 'auth/invalid-credential':
        // Firebase v10+ uses this instead of wrong-password/user-not-found
        return t('login.errors.wrong_password');
      case 'auth/invalid-email':
        return t('login.errors.invalid_email');
      case 'auth/too-many-requests':
        return t('login.errors.too_many_requests');
      case 'validation/empty_fields':
        return t('login.please_complete_fields');
      case 'validation/empty_email':
        return t('login.please_enter_email');
      default:
        return t('login.errors.general');
    }
  };

  const getSuccessMessage = (code: string) => {
    if (!code) return '';
    
    switch (code) {
      case 'password_reset_sent':
        return t('login.check_email_reset');
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4 relative">
      {/* Selector de idioma en esquina inferior derecha */}
      <div className="absolute bottom-4 right-4 z-10">
        <LanguageSelector />
      </div>

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="mx-auto w-32 h-32 flex items-center justify-center mb-6">
            <img 
              src="/Logo_pulpou_vertical.svg" 
              alt="Pulpou" 
              className="h-28 w-auto"
            />
          </div>
        </div>

        {/* Formulario */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Campo Email */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                {t('login.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EC4899] focus:border-transparent transition-all"
                  placeholder={t('login.email_placeholder')}
                  required
                />
              </div>
            </div>

            {/* Campo Contraseña */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                {t('login.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EC4899] focus:border-transparent transition-all"
                  placeholder={t('login.password_placeholder')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Mensajes de error y éxito */}
            {errorCode && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{getErrorMessage(errorCode)}</p>
              </div>
            )}

            {successMessage && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-600">{getSuccessMessage(successMessage)}</p>
              </div>
            )}

            {/* Botón principal */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#EC4899] text-white py-3 px-4 rounded-lg font-medium hover:bg-[#db2777] focus:ring-2 focus:ring-[#EC4899] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>{t('login.sign_in')}</span>
                </>
              )}
            </button>

            {/* ¿Olvidaste tu contraseña? */}
            <button
              type="button"
              onClick={handleForgotPassword}
              className="w-full text-sm text-[#EC4899] hover:text-[#db2777] transition-colors"
              disabled={loading}
            >
              {t('login.forgot_password')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login; 